import { CheckedItemStatus, IcheckedItems, IinstallBase, IjobWorkOrder, Iproduct } from '../models';
import { jobTypesIdEnum } from '../models-enums';

/*
 * MMG-4433: repair-type jobs may no longer close with only the SAP placeholder ("virtual")
 * asset on them. These helpers feed the Final Check row that enforces it.
 */

// Job types where the FSE picks/creates assets for the job, so a real asset can be required.
const assetSelectionJobTypeIDs: string[] = [
    jobTypesIdEnum.repair,
    jobTypesIdEnum.commissioning,
    jobTypesIdEnum.warrantyBM,
    jobTypesIdEnum.servicePlan
];

export const realAssetCheckApplies = (jobTypeID?: string): boolean =>
    !!jobTypeID && assetSelectionJobTypeIDs.includes(jobTypeID);

/*
 * True when at least one work order on the job points at a non-virtual asset.
 * Reads the job's work orders plus the master install-base / product maps - never the
 * filtered inventory list, which table filters and the hide-virtual toggle change.
 * An asset that is not on the device (deleted, or never synced) is not on the job's asset
 * list either, so it does not count. An asset whose product is unknown does count - a
 * catalog gap must not block a close.
 */
export const hasRealAssetOnJob = (
    jobWorkOrders: IjobWorkOrder[],
    installBasesByID: { [id: string]: IinstallBase },
    productsByID: { [id: string]: Iproduct },
    virtualSubcategoryID: string
): boolean => {
    for (const jobWorkOrder of jobWorkOrders) {
        if (jobWorkOrder.isDeleted === true) {
            continue;
        }

        // The live selector substitutes initialWorkOrder (installBaseID '') for an unsynced WO,
        // which then lands in the missing-install-base branch below.
        const workOrder = jobWorkOrder.workOrder;
        if (!workOrder || workOrder.isDeleted === true) {
            continue;
        }

        const installBase = installBasesByID[workOrder.installBaseID];
        if (!installBase || installBase.isDeleted === true) {
            continue;
        }

        const product = productsByID[installBase.materialID];
        if (!product || product.subcategoryID !== virtualSubcategoryID) {
            return true;
        }
    }

    return false;
};

export const realAssetCheckID = 'realAssetAdded';

// MMG-4543: the Final Check offers "Ignore the real asset check?" only while this row is red.
export const isRealAssetCheckFailing = (items: IcheckedItems[]): boolean =>
    items.some(item => item.id === realAssetCheckID && item.passed === CheckedItemStatus.fail);

// MMG-4543: when the FSE chooses to ignore it, the red real-asset row counts as passed.
export const applyRealAssetCheckIgnore = <T extends IcheckedItems>(items: T[], ignore: boolean): T[] =>
    ignore
        ? items.map(item =>
              item.id === realAssetCheckID && item.passed === CheckedItemStatus.fail
                  ? { ...item, passed: CheckedItemStatus.pass }
                  : item
          )
        : items;
